// One folder on a person's page: its records, and the scans filed against them.
//
// A record is the FACT ("Mick holds an HR licence, number 0123456, expiring in
// March"); a document is the scan of it. That distinction is the whole feature:
// a person with a ticket and no scan of it still has the ticket, still turns up
// on the reminders when it lapses, and still counts when somebody asks who can
// do the work on Tuesday. So records lead, and their scans hang off them.
//
// Files nobody has filed against a record are still listed, underneath, rather
// than hidden — that is where everything uploaded before folders existed lands,
// and where a scan drops back to when its record is deleted.
import { useMemo, useState } from 'react';
import { ApiError } from '../../api';
import { contentUrl, documents, type Attachment } from '../../documentsApi';
import { people, type CompetencyCode, type InductionCode, type PersonRecord } from '../../peopleApi';
import type { PersonSection } from '../../lib/personSections';
import { formatDMY } from '../../lib/dates';
import { StatusBadge } from '../ui';
import { SortHeader } from '../ui/SortHeader';
import { sortRows, useSort } from '../../lib/sortRows';
import { DocumentViewer } from '../DocumentViewer';
import { formatSize } from '../Documents';
import { RecordModal } from './RecordModal';
import { DownloadLink } from '../DownloadLink';

type SortKey = 'title' | 'issued' | 'expires';

export function RecordsSection({
  section,
  resourceId,
  records,
  files,
  codes,
  inductions,
  canEdit,
  onChanged,
  onError,
}: {
  section: PersonSection;
  resourceId: string;
  records: PersonRecord[];
  /** Every document in this folder — linked and loose. */
  files: Attachment[];
  codes: CompetencyCode[];
  inductions: InductionCode[];
  canEdit: boolean;
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState<PersonRecord | 'new' | null>(null);
  const [viewing, setViewing] = useState<Attachment | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // By expiry: the question this panel exists to answer is what lapses next,
  // and a record with no expiry sorts to the bottom on its own.
  const { sortKey, sortDir, onSort } = useSort<SortKey>('expires');

  const loose = files.filter((f) => f.personRecordId === null);
  const scansFor = (recordId: string) => files.filter((f) => f.personRecordId === recordId);

  const sorted = useMemo(
    () =>
      sortRows(
        records,
        (record) => {
          switch (sortKey) {
            case 'title':
              return record.title;
            case 'issued':
              return record.issueDate;
            case 'expires':
              return record.expiresOn;
          }
        },
        sortDir
      ),
    [records, sortKey, sortDir]
  );

  async function remove(record: PersonRecord) {
    if (
      !window.confirm(
        `Remove “${record.title}”? Any scans filed against it stay in this folder.`
      )
    ) {
      return;
    }
    setBusy(record.id);
    try {
      await people.removeRecord(resourceId, record.id);
      onChanged('Removed.');
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not remove that.');
    } finally {
      setBusy(null);
    }
  }

  async function unlink(file: Attachment) {
    try {
      await documents.update(file.id, { recordId: null });
      onChanged('Unlinked. The file is still in this folder.');
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not unlink that.');
    }
  }

  /** The scans hanging off one record — shared by the card and the table. */
  const scanLinks = (record: PersonRecord) => {
    const scans = scansFor(record.id);
    if (scans.length === 0) return null;
    return (
      <ul className="m-0 mt-1 list-none space-y-0.5 p-0 text-sm">
        {scans.map((file) => (
          <li key={file.id} className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="table-link inline-flex min-h-11 items-center text-left sm:min-h-0"
              // The row opens the record's editor; a scan opens the scan. Both
              // are useful, so the inner one stops the click reaching the row.
              onClick={(event) => {
                event.stopPropagation();
                setViewing(file);
              }}
            >
              📎 {file.title}
            </button>
            <span className="text-xs text-gray-500">{formatSize(file.sizeBytes)}</span>
            <DownloadLink
              href={contentUrl(file.id, { download: true })}
              className="row-action"
              onClick={(event) => event.stopPropagation()}
            >
              Download
            </DownloadLink>
            {canEdit && (
              <button
                type="button"
                className="row-action"
                onClick={(event) => {
                  event.stopPropagation();
                  void unlink(file);
                }}
              >
                Unlink
              </button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section className="panel">
      {/* A real section HEADER, not another row of content: the heading, its
          count and the add button sit above a rule, so the eye reads "here is
          a section and here is what it holds" rather than meeting the first
          record straight after a line of text. */}
      <div className="row mb-4 border-b border-gray-200 pb-3">
        <h2 className="!mb-0">
          {section.label}
          {records.length > 0 && <span className="muted"> ({records.length})</span>}
        </h2>
        <span className="spacer" />
        {canEdit && (
          <button type="button" className="btn-secondary btn-sm" onClick={() => setEditing('new')}>
            {section.addLabel}
          </button>
        )}
      </div>

      {records.length === 0 ? (
        <p className="muted">{section.empty}</p>
      ) : (
        <>
          {/* Phones keep the stacked cards; the table below is the desktop
              half of the same list (phase 8's DM3 pair). */}
          <ul className="m-0 list-none space-y-3 p-0 sm:hidden">
            {sorted.map((record) => (
              <li key={record.id} className="border-t border-gray-200 pt-3 first:border-0 first:pt-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{record.title}</span>
                      {/* The fixed 30-day chip, which is deliberately not the
                          company's reminder window — one of them says "start
                          dealing with this", the other "this is nearly out"
                          (spec 03). */}
                      {record.expiresOn ? (
                        <StatusBadge kind="expiry" status={record.expiry} />
                      ) : (
                        <span className="text-xs text-gray-500">No expiry</span>
                      )}
                      {record.codeArchived && (
                        <span className="text-xs text-gray-500">(code archived)</span>
                      )}
                    </div>

                    <div className="mt-0.5 text-sm text-gray-600">
                      {[
                        record.recordNumber && `No. ${record.recordNumber}`,
                        record.issueDate && `Issued ${formatDMY(record.issueDate)}`,
                        record.expiresOn && `Expires ${formatDMY(record.expiresOn)}`,
                      ]
                        .filter(Boolean)
                        .join(' · ') || <span className="muted">—</span>}
                    </div>

                    {record.notes && (
                      <div className="mt-0.5 text-sm text-gray-600">{record.notes}</div>
                    )}

                    {scanLinks(record)}
                  </div>

                  {canEdit && (
                    <div className="flex flex-none flex-wrap justify-end gap-1">
                      <button type="button" className="row-action" onClick={() => setEditing(record)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="row-action-danger"
                        disabled={busy === record.id}
                        onClick={() => void remove(record)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="table-wrap max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader
                    label={section.recordCategory === 'competency' ? 'Competency' : 'Induction'}
                    sortKey="title"
                    current={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                  />
                  <th scope="col" className="table-header">
                    Number
                  </th>
                  <SortHeader label="Issued" sortKey="issued" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Expires" sortKey="expires" current={sortKey} dir={sortDir} onSort={onSort} />
                  {canEdit && (
                    <th scope="col" className="table-header w-px">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sorted.map((record) => (
                  <tr
                    key={record.id}
                    // The whole row opens the record — the desktop half of the
                    // card's whole-card tap target (LinkedRow's rule). Not a
                    // LinkedRow itself, because a record's destination is a
                    // dialog rather than a URL. A reader who cannot edit gets
                    // the table without the affordance, since there is nothing
                    // behind the click for them.
                    className={canEdit ? 'cursor-pointer hover:bg-gray-50' : undefined}
                    onClick={canEdit ? () => setEditing(record) : undefined}
                  >
                    <td className="table-cell font-medium">
                      <div className="flex flex-wrap items-center gap-2">
                        <span>{record.title}</span>
                        {record.codeArchived && (
                          <span className="text-xs font-normal text-gray-500">(code archived)</span>
                        )}
                      </div>
                      {record.notes && (
                        <div className="mt-0.5 text-xs font-normal text-gray-600">{record.notes}</div>
                      )}
                      {scanLinks(record)}
                    </td>
                    <td className="table-cell table-muted whitespace-nowrap">
                      {record.recordNumber || '—'}
                    </td>
                    <td className="table-cell table-muted whitespace-nowrap">
                      {record.issueDate ? formatDMY(record.issueDate) : '—'}
                    </td>
                    <td className="table-cell whitespace-nowrap">
                      {record.expiresOn ? (
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="table-muted">{formatDMY(record.expiresOn)}</span>
                          <StatusBadge kind="expiry" status={record.expiry} />
                        </span>
                      ) : (
                        <span className="table-muted">No expiry</span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="table-cell w-px whitespace-nowrap text-right">
                        <button
                          type="button"
                          className="row-action"
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditing(record);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="row-action-danger"
                          disabled={busy === record.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            void remove(record);
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Loose files. Listed rather than hidden: this is where everything
          uploaded before folders existed sits, and where a scan drops back to
          when its record is deleted. */}
      {loose.length > 0 && (
        <>
          <h3 className="mt-6 text-sm font-medium text-gray-700">
            Not filed against a {section.recordCategory === 'competency' ? 'competency' : 'record'}
          </h3>
          <ul className="m-0 list-none space-y-1 p-0 text-sm">
            {loose.map((file) => (
              <li key={file.id} className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="table-link inline-flex min-h-11 items-center text-left sm:min-h-0"
                  onClick={() => setViewing(file)}
                >
                  📎 {file.title}
                </button>
                <span className="text-xs text-gray-500">
                  {formatSize(file.sizeBytes)} · added{' '}
                  {new Date(file.uploadedAt).toLocaleDateString('en-AU')}
                </span>
                <DownloadLink href={contentUrl(file.id, { download: true })} className="row-action">
                  Download
                </DownloadLink>
              </li>
            ))}
          </ul>
        </>
      )}

      {editing && (
        <RecordModal
          resourceId={resourceId}
          section={section}
          codes={codes}
          inductions={inductions}
          record={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            onChanged(message);
          }}
        />
      )}

      {viewing && <DocumentViewer attachment={viewing} onClose={() => setViewing(null)} />}
    </section>
  );
}
